import type React from 'react';
import type { FollowUp } from '../lib/api.ts';
import { channelAr, priorityAr, priorityTone, resultAr, statusAr, statusTone , sourceInfo } from '../lib/labels.ts';
import { cairoStamp, shortDate } from '../lib/format.ts';
import { PhoneActions } from '../components/PhoneActions.tsx';
import { Badge, Empty } from '../components/ui.tsx';
import { CoachingBanner, CoachingChip } from './Coaching.tsx';

interface Props {
  items: FollowUp[]; onOpen: (f: FollowUp) => void; onBook?: (f: FollowUp) => void; showSeller?: boolean; empty?: string;
  /** **الضغط يفلتر** (قرار المالك 01/10/2026): اسم البياع، والأولوية، والموقف، والنتيجة — كلٌّ يعرض ما يشبهه وحده */
  onFilter?: (f: ListFilter) => void;
  /** «تحديث موقف المتابعة» (0151، قرار المالك 01/10/2026: «بدل سجل جديد، خلّيها تحديث موقف المتابعة») */
  onDone?: (f: FollowUp) => void;
  /** «💡 توجيه» على العميل (0191) — للإدارة ولقارئ متابعات الفريق؛ وغيابه يعني أن القائمة للبياع فيرى توجيهاته هو */
  onCoach?: (f: FollowUp) => void;
  /** البياع علّم «نفّذته» — تُعاد قراءة القائمة */
  onCoachingChanged?: () => void;
}

/** قائمة سريعة القراءة: جدول على الشاشات الكبيرة وبطاقات على الجوال (نفس البيانات). */
/** حُجز العميل؟ المتابعة المحوَّلة، أو حجزٌ بالرقم نفسه منذ يوم التواصل (01/10/2026) */
/** شكلٌ واحد لأزرار الصفّ: ارتفاعٌ ثابت ونصٌّ في المنتصف (06/10/2026) */
const ACT = 'inline-flex h-7 items-center justify-center gap-1 whitespace-nowrap rounded-md px-1.5 text-[11px] font-semibold leading-none';
const booked = (f: FollowUp): boolean => f.convertedToBooking || !!f.bookedBookingNo;
function BookedBadge({ f }: { f: FollowUp }) {
  const no = f.bookingNo ?? f.bookedBookingNo;
  return (
    <span className="inline-flex flex-col items-center rounded-lg bg-emerald-600 px-2 py-1 text-center text-xs font-semibold leading-tight text-white">
      <span>✓ تم الحجز</span>
      {no && <span className="font-mono text-[10px] font-normal opacity-90" dir="ltr">{no}</span>}
    </span>
  );
}

/** موقفٌ قديم (0151): تحدّث بعده — يظهر في السجلّ وحده، فالقوائم تعرض آخر موقف */
const DoneBadge = () => (
  <span className="inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold text-ink-600">موقف قديم — اتحدّث بعده</span>
);
/**
 * **التاريخ والساعة** (قرار المالك 01/10/2026: «عاوز أضيف مع التاريخ الوقت كمان علشان أعرف المتابعة دي اتسجّلت إمتى»):
 * يوم التواصل وساعة تسجيلها؛ ولو سُجّلت في يومٍ غير يوم التواصل (تسجيلٌ متأخّر) يُقال يوم التسجيل كاملًا.
 */
function WhenRecorded({ f }: { f: FollowUp }) {
  const s = cairoStamp(f.createdAt);
  if (!s) return <bdi className="tabular-nums">{shortDate(f.contactedOn)}</bdi>;
  return s.day === f.contactedOn
    ? <bdi className="tabular-nums">{shortDate(f.contactedOn)} · {s.time}</bdi>
    : <><bdi className="tabular-nums">{shortDate(f.contactedOn)}</bdi><span className="block text-[11px] text-ink-500">اتسجّلت <bdi className="tabular-nums">{s.date} · {s.time}</bdi></span></>;
}

/**
 * **«📞 بنك الأرقام»** (قرار المالك 01/10/2026: «لازم يميّز بين الأرقام اللي متسجّلة من بنك الأرقام ولا من التسجيل العادي…
 * علشان الحجز اللي هيتم من بنك الأرقام عليه مكافأة»): الشارة بنفس شرط المكافأة على الخادم — فما عليه الشارة يُكافأ حجزه.
 */
/** شارة المصدر (01/10/2026) — «📞 بنك الأرقام» منها بنفس شرط المكافأة */
export function SourceBadge({ f }: { f: FollowUp }) {
  const info = sourceInfo(f.source ?? (f.fromLeadBank ? 'LEAD_BANK' : null));
  if (!info) return null;
  return (
    <span title={info.code === 'LEAD_BANK' ? 'الرقم من بنك الأرقام — الحجز منه عليه مكافأة البنك' : `المصدر: ${info.ar}`}
      className={`inline-flex items-center whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-semibold ${info.tone}`}>{info.icon} {info.ar}</span>
  );
}

export const LeadBankBadge = () => (
  <span title="الرقم من بنك الأرقام — الحجز منه عليه مكافأة البنك"
    className="inline-flex items-center whitespace-nowrap rounded-full bg-cyan-100 px-2 py-0.5 text-[11px] font-semibold text-cyan-800">📞 بنك الأرقام</span>
);

/** مفتوحة = لا حُجزت ولا تحدّث موقفها بعدها — وعليها وحدها «تحديث موقف المتابعة» */
const isOpen = (f: FollowUp) => !f.closedAt && !booked(f);
/** رقم المتابعة كما يعرفه البياع: رقمها الأوّل — فالتحديث لا يغيّر رقمها في عينه */
const shownNo = (f: FollowUp) => f.firstFollowUpNo ?? f.followUpNo;
/** «تم متابعته» على الكارت (قرار المالك: «يظهر إن العميل ده تم متابعته»): اتابع النهاردة، أو كم مرّة اتحدّث */
function UpdatesBadge({ f }: { f: FollowUp }) {
  const n = (f.updatesCount ?? 1) - 1;
  if (n < 1) return null;
  const today = new Date().toLocaleDateString('en-CA', { timeZone: 'Africa/Cairo' });
  if (f.contactedOn === today) {
    return <span className="inline-flex items-center whitespace-nowrap rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-semibold text-emerald-800">✓ اتابع النهاردة</span>;
  }
  const times = n === 1 ? 'مرة' : n === 2 ? 'مرتين' : `${n} مرات`;
  return <span className="inline-flex items-center whitespace-nowrap rounded-full bg-sky-100 px-2 py-0.5 text-[11px] font-semibold text-sky-800">🔄 اتحدّثت {times}</span>;
}

// day وconverted من كارت اليوم (03/10/2026): يومٌ بعينه، و«اتحوّلت لحجز»
export type ListFilter = { kind: 'seller' | 'status' | 'priority' | 'result' | 'source' | 'day' | 'converted' | 'contact' | 'counted'; value: string; label: string };

/** المصدر يُضغط فيفلتر كغيره (01/10/2026: «نضغط على بنك الأرقام يظهر البنك فقط») */
function SourcePick({ f, on }: { f: FollowUp; on?: (f: ListFilter) => void }) {
  const info = sourceInfo(f.source ?? (f.fromLeadBank ? 'LEAD_BANK' : null));
  if (!info) return null;
  return <Pick f={{ kind: 'source', value: info.code, label: info.ar }} on={on}><SourceBadge f={f} /></Pick>;
}

/**
 * **قيمةٌ تُضغط فتفلتر** (قرار المالك 01/10/2026: «لما أضغط على اسم البياع في قوائم المتابعات يظهرلي ما يخصّ البياع ده بس…
 * وكذلك لو ضغطت على مهمة يجيبلي كل العملاء المهمة، ولو ضغطت على غير مهتم يجيبلي كل الغير مهتم»). بلا onFilter تبقى نصًّا.
 */
function Pick({ f, on, children, className = '' }: { f: ListFilter; on?: (f: ListFilter) => void; children: React.ReactNode; className?: string }) {
  if (!on) return <span className={className}>{children}</span>;
  return (
    <button type="button" onClick={() => on(f)} title={`اعرض ${f.label} بس`} aria-label={`فلترة: ${f.label}`}
      className={`cursor-pointer rounded-md underline-offset-2 transition hover:underline hover:opacity-80 ${className}`}>{children}</button>
  );
}

export function FollowUpList({ items, onOpen, onBook, onDone, onFilter, showSeller = false, empty = 'لا توجد متابعات', onCoach, onCoachingChanged }: Props) {
  const coaching = (f: FollowUp) => onCoach ? <CoachingChip f={f} /> : <CoachingBanner f={f} onChanged={() => onCoachingChanged?.()} />;
  if (!items.length) return <Empty>{empty}</Empty>;

  return (
    <>
      {/*
       * التنسيق (قرار المالك: «ظبّط التنسيق البايظ وخليه مريح للعين»).
       *
       * كانت عشرة أعمدة متساوية الضغط فينكسر اسم البياع على ثلاثة أسطر وتتراكب
       * الملاحظات، والقيم في أعلى الخليّة (`align-top`) بينما الشارات في وسطها —
       * فيبدو الصفّ مائلًا. والإصلاح ثلاثة:
       *   · **دمجٌ لما يُقرأ معًا**: الهاتف تحت اسم صاحبه، والنوع تحت وقت التواصل،
       *     ونتيجة التواصل مع موقف العميل — فتسع أعمدة تصير ستّة.
       *   · **`align-middle`** لكل الخلايا: سطرٌ واحد تستقرّ عليه العين.
       *   · **`nowrap` لما لا يُكسر** (الاسم · البياع · التواريخ)، وسقفٌ للملاحظات وحدها.
       */}
      {/* بلا تمرير جانبي أصلًا (قرار المالك 01/10/2026: «مش عاوز شريط أصلًا»): الجدول من الشاشات العريضة (xl) بأعمدةٍ
          محسوبة ورقمُ المتابعة تحت الاسم، وما دونها بطاقاتٌ تسع العرض — فلا يتجاوز الجدول عرضه في أي مقاس */}
      <div>
        <table className="hidden w-full table-fixed text-right text-sm leading-snug xl:table">
          <thead className="text-xs text-ink-600">
            <tr className="border-b-2 border-slate-200">
              <th className="w-[11rem] px-2 py-2.5 font-semibold">العميل</th>
              {showSeller && <th className="w-[7.5rem] px-2 py-2.5 font-semibold">البياع</th>}
              <th className="w-[10rem] whitespace-nowrap px-2 py-2.5 font-semibold">التواصل</th>
              <th className="w-[12.5rem] px-2 py-2.5 font-semibold">النتيجة والموقف</th>
              <th className="px-2 py-2.5 font-semibold">الملاحظات</th>
              <th className="w-[10rem] whitespace-nowrap px-2 py-2.5 font-semibold">المتابعة القادمة</th>
              <th className="w-[9rem] px-2 py-2.5 font-semibold"><span className="sr-only">إجراءات</span></th>
            </tr>
          </thead>
          <tbody>
            {items.map(f => (
              <tr key={f.id} className="border-b border-slate-100 align-middle last:border-0 odd:bg-slate-50/40 hover:bg-brand-50/50">
                <td className="break-words px-2 py-2">
                  <div className="font-medium text-ink-900">{f.customerName}</div>
                  <div className="mt-0.5"><SourcePick f={f} on={onFilter} /></div>
                  <div className="mt-0.5 text-xs tabular-nums text-ink-500" dir="ltr"><PhoneActions phone={f.phone} /></div>
                  {f.altPhone && <div className="text-xs tabular-nums text-ink-500" dir="ltr"><PhoneActions phone={f.altPhone} /></div>}
                  <div className="mt-0.5 font-mono text-[10px] text-ink-400" dir="ltr">{shownNo(f)}</div>
                </td>
                {showSeller && <td className="break-words px-2 py-2 text-ink-700"><Pick f={{ kind: 'seller', value: f.salesUserId, label: f.salesName }} on={onFilter} className="text-start">{f.salesName}</Pick></td>}
                <td className="whitespace-nowrap px-2 py-2 text-ink-700">
                  {/* باليوم فقط (قرار المالك 01/10/2026: «شيل ساعة التواصل دي») — الساعة محفوظةٌ ولا تُعرض */}
                  <div><WhenRecorded f={f} /></div>
                  <div className="mt-0.5 text-xs text-ink-500">{channelAr(f.channel)}</div>
                </td>
                <td className="px-2 py-2">
                  {/* الموقف وشارة «اتابع النهاردة» في سطرٍ واحد — الصفّ أقصر (01/10/2026) */}
                  <div className="flex flex-wrap items-center gap-1">
                    <Pick f={{ kind: 'status', value: f.customerStatus, label: statusAr(f.customerStatus) }} on={onFilter}><Badge tone={statusTone[f.customerStatus]}>{statusAr(f.customerStatus)}</Badge></Pick>
                    {f.closedAt ? <DoneBadge /> : <UpdatesBadge f={f} />}
                  </div>
                  <div className="mt-0.5 text-xs text-ink-600"><Pick f={{ kind: 'result', value: f.result, label: resultAr(f.result) }} on={onFilter}>{resultAr(f.result)}</Pick></div>
                </td>
                {/* الملاحظات وحدها تُكسر وتُقصّ: سطران يكفيان لمعرفة الموضوع، والتفصيل في «تفاصيل» */}
                <td className="px-2 py-2 text-ink-600">
                  {f.notes ? <span className="line-clamp-2 break-words" title={f.notes}>{f.notes}</span> : <span className="text-ink-400">—</span>}
                  {coaching(f)}
                </td>
                <td className="whitespace-nowrap px-2 py-2">
                  {f.nextFollowUpOn ? (
                    <>
                      <div className={`tabular-nums ${f.isOverdue ? 'font-semibold text-rose-700' : 'text-ink-700'}`}>
                        {shortDate(f.nextFollowUpOn)}
                      </div>
                      <div className="mt-1 flex items-center gap-1.5">
                        <Pick f={{ kind: 'priority', value: f.priority, label: priorityAr(f.priority) }} on={onFilter}><Badge tone={priorityTone[f.priority]}>{priorityAr(f.priority)}</Badge></Pick>
                        {f.isOverdue && <span className="text-xs font-semibold text-rose-700">متأخرة</span>}
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="text-ink-400">—</div>
                      <div className="mt-1"><Pick f={{ kind: 'priority', value: f.priority, label: priorityAr(f.priority) }} on={onFilter}><Badge tone={priorityTone[f.priority]}>{priorityAr(f.priority)}</Badge></Pick></div>
                    </>
                  )}
                </td>
                <td className="px-2 py-2">
                  {/* أزرار صغيرة (قرار المالك 01/10/2026: «صغّرها شوية وخلّيها تحديث الموقف فقط… بدل ما المتابعة الواحدة
                      كبيرة كده وواخدة مساحة كبيرة»): «تحديث الموقف» سطرٌ واحد، و«تفاصيل» و«حجز» جنب بعض تحته */}
                  {/* **شبكة أزرار منضبطة** (المالك 06/10/2026: «اعمل تنسيق جيد للمنظر والكلام دا»): كل زرٍّ بنفس الارتفاع والعرض،
                      نصّه في المنتصف، وصفّان لا أكثر — «تحديث الموقف» و«إنشاء حجز» كلٌّ على عرض الخانة، و«توجيه» و«تفاصيل» نصفين */}
                  <div className="grid grid-cols-2 gap-1">
                    {onDone && isOpen(f) && (
                      <button type="button" className={`${ACT} col-span-2 bg-emerald-600 text-white hover:bg-emerald-700`} onClick={() => onDone(f)}>🔄 تحديث الموقف</button>
                    )}
                    {booked(f)
                      ? <div className="col-span-2 flex justify-center"><BookedBadge f={f} /></div>
                      : onBook && <button type="button" className={`${ACT} col-span-2 bg-brand-600 text-white hover:bg-brand-700`} onClick={() => onBook(f)}>إنشاء حجز</button>}
                    {onCoach && <button type="button" className={`${ACT} border border-amber-300 bg-amber-50 text-amber-900 hover:bg-amber-100`} onClick={() => onCoach(f)}>💡 توجيه</button>}
                    <button type="button" className={`${ACT} ${onCoach ? '' : 'col-span-2'} border border-slate-300 bg-white text-ink-700 hover:bg-slate-50`} onClick={() => onOpen(f)}>تفاصيل</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <ul className="space-y-2 xl:hidden">
        {items.map(f => (
          <li key={f.id} className="rounded-lg border border-slate-200 p-3">
            <div className="flex items-start justify-between gap-2">
              <div>
                <div className="flex flex-wrap items-center gap-1.5 font-medium">{f.customerName}<SourcePick f={f} on={onFilter} /></div>
                <div className="text-sm tabular-nums text-ink-500" dir="ltr"><PhoneActions phone={f.phone} /></div>
                {f.altPhone && <div className="text-sm tabular-nums text-ink-500" dir="ltr"><PhoneActions phone={f.altPhone} /></div>}
              </div>
              <div className="flex flex-col items-end gap-1">
                <Pick f={{ kind: 'status', value: f.customerStatus, label: statusAr(f.customerStatus) }} on={onFilter}><Badge tone={statusTone[f.customerStatus]}>{statusAr(f.customerStatus)}</Badge></Pick>
                {f.closedAt ? <DoneBadge /> : <UpdatesBadge f={f} />}
              </div>
            </div>
            {/* كل معلومةٍ في خانتها: التاريخ أرقامٌ لاتينية وسط سطرٍ عربي كان يقلب ترتيب السطر على الموبايل */}
            <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-ink-700">
              <span>التواصل: <WhenRecorded f={f} /></span>
              <span className="text-ink-400">·</span><span>{channelAr(f.channel)}</span>
              <span className="text-ink-400">·</span><Pick f={{ kind: 'result', value: f.result, label: resultAr(f.result) }} on={onFilter}>{resultAr(f.result)}</Pick>
            </div>
            {f.notes && <p className="mt-1 text-sm text-ink-500">{f.notes}</p>}
            <div className="mt-2 flex flex-wrap items-center gap-2 text-sm">
              {showSeller && <Pick f={{ kind: 'seller', value: f.salesUserId, label: f.salesName }} on={onFilter} className="text-ink-500">{f.salesName}</Pick>}
              {f.nextFollowUpOn && (
                <span className={f.isOverdue ? 'font-medium text-rose-700' : 'text-ink-700'}>
                  المتابعة القادمة: <bdi className="tabular-nums">{shortDate(f.nextFollowUpOn)}</bdi>{f.isOverdue ? ' (متأخرة)' : ''}
                </span>
              )}
              <Pick f={{ kind: 'priority', value: f.priority, label: priorityAr(f.priority) }} on={onFilter}><Badge tone={priorityTone[f.priority]}>{priorityAr(f.priority)}</Badge></Pick>
            </div>
            {onDone && isOpen(f) && (
              <button type="button" className="mt-2 w-full rounded-lg bg-emerald-600 py-2.5 text-sm font-bold text-white active:bg-emerald-700"
                onClick={() => onDone(f)}>🔄 تحديث الموقف</button>
            )}
            {coaching(f)}
            <div className="mt-2 flex gap-2">
              {onCoach && <button type="button" className="flex-1 rounded-lg border border-amber-300 bg-amber-50 py-2 text-sm font-semibold text-amber-900" onClick={() => onCoach(f)}>💡 توجيه</button>}
              <button type="button" className="btn-ghost flex-1 py-2 text-sm" onClick={() => onOpen(f)}>تفاصيل</button>
              {booked(f)
                ? <BookedBadge f={f} />
                : onBook && <button type="button" className="btn-primary flex-1 py-2 text-sm" onClick={() => onBook(f)}>إنشاء حجز</button>}
            </div>
          </li>
        ))}
      </ul>
    </>
  );
}
